import Flex from '@components/Flex/Flex';
import {
  DatePicker as DatePickerMUI,
  DatePickerProps as DatePickerPropsMUI,
} from '@mui/x-date-pickers/DatePicker';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { CalendarIcon, DayPicker, StyledInput } from './DatePicker.styles';
import { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { InputAdornment, useTheme } from '@mui/material';
import { ActionBar } from './ActionBar/ActionBar';
import { PickersActionBarAction } from '@mui/x-date-pickers/PickersActionBar/PickersActionBar';
import React, { useLayoutEffect, useState } from 'react';

interface DatePickerProps
  extends Omit<DatePickerPropsMUI<any, any>, 'renderInput'> {
  name?: string;
  actions?: PickersActionBarAction[];
  mobileActions?: PickersActionBarAction[];
  locked?: boolean;
  error?:
    | Merge<FieldError, FieldErrorsImpl<{ value: number; name: string }>>
    | undefined;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      disabled,
      error,
      actions = [],
      mobileActions = [],
      locked = false,
      ...props
    },
    ref
  ) => {
    const intl = useIntl();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    useLayoutEffect(() => {
      const onWindowResize = (): void => {
        setAnchorEl(null);
      };
      window.addEventListener('resize', onWindowResize);
      onWindowResize();

      return () => window.removeEventListener('resize', onWindowResize);
    }, []);

    const renderPickerDay = (
      _date: Date,
      _selectedDates: Array<Date | null>,
      pickersDayProps: PickersDayProps<Date>
    ) => {
      return <DayPicker disableRipple {...pickersDayProps} />;
    };

    const showIconError = error && !isFocused && !isOpen;

    return (
      <DatePickerMUI
        inputRef={ref}
        {...props}
        disabled={disabled || locked}
        views={['year', 'month', 'day']}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <CalendarIcon
                hasValue={!!props.value}
                style={{
                  color: showIconError ? theme.palette.error.main : undefined,
                }}
              />
            </InputAdornment>
          ),
          ...props.InputProps,
        }}
        components={{
          OpenPickerIcon: () => (
            <Flex marginRight={2}>
              <CalendarIcon
                hasValue={!!props.value}
                style={{
                  color: showIconError ? theme.palette.error.main : undefined,
                }}
              />
            </Flex>
          ),
          ActionBar: ActionBar,
        }}
        componentsProps={{
          actionBar: {
            actions: (variant) =>
              variant === 'desktop' ? actions : mobileActions,
          },
        }}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        renderInput={({ ...params }) => {
          return (
            <StyledInput
              name={props.name}
              {...params}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'date-picker-input',
              }}
              onClick={handleClick}
              onFocus={() => setIsFocused(true)}
              open={isOpen}
              locked={locked}
              {...getErrorProps(intl, error)}
            />
          );
        }}
        renderDay={renderPickerDay}
        PopperProps={{
          placement: 'bottom-end',
          anchorEl,
        }}
      />
    );
  }
);

export default DatePicker;
